import type { PaginationMeta } from "@chotca/config";
import { z } from "zod";
import { ChotcaApiError } from "./errors";
import { parseApiErrorBody, parseSuccessEnvelope } from "./parse-envelope";

export type HttpMethod = "GET" | "POST" | "PATCH" | "DELETE";

export type RequestLogInfo = {
  method: HttpMethod;
  path: string;
  query?: Record<string, QueryValue>;
  auth: boolean;
};

export type ResponseLogInfo = RequestLogInfo & {
  status: number;
  durationMs: number;
};

export type ErrorLogInfo = RequestLogInfo & {
  status?: number;
  durationMs: number;
  error: unknown;
};

export type RequestContext = {
  baseUrl: string;
  getAccessToken: () => Promise<string | null | undefined>;
  // Extra headers attached to every request (e.g. X-Chotca-App).
  extraHeaders?: Record<string, string>;
  // Like extraHeaders, but evaluated per request — use this when the value
  // depends on app state that can change at runtime (e.g. current UI locale).
  getExtraHeaders?: () => Record<string, string> | undefined;
  // Called when the server returns 401 on an authed request. If the handler
  // refreshes the access token successfully it should return `true`; the SDK
  // will then retry the original request once with the new token. Any other
  // return value (void / false) → the 401 propagates as an error.
  onUnauthorized?: () => boolean | void | Promise<boolean | void>;
  timeoutMs: number;
  // Observability hooks. Fire around every request — wire to your logger
  // of choice. Errors thrown from these hooks are swallowed so logging
  // never breaks the request path.
  onRequest?: (info: RequestLogInfo) => void;
  onResponse?: (info: ResponseLogInfo) => void;
  onError?: (info: ErrorLogInfo) => void;
};

function safeCall(fn: (() => void) | undefined): void {
  if (!fn) return;
  try {
    fn();
  } catch {
    // observability must never break requests
  }
}

export type QueryValue = string | number | boolean | undefined | string[];

function buildQuery(query?: Record<string, QueryValue>): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      for (const v of value) {
        params.append(key, v);
      }
    } else {
      params.set(key, String(value));
    }
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      ...init,
      credentials: "include",
      signal: controller.signal,
    });
  } finally {
    clearTimeout(id);
  }
}

export type JsonRequestOptions<T> = {
  method: HttpMethod;
  path: string;
  query?: Record<string, QueryValue>;
  body?: unknown;
  dataSchema: z.ZodType<T>;
  auth: boolean;
  allowEmpty?: boolean;
};

async function sendOnce<T>(
  ctx: RequestContext,
  options: JsonRequestOptions<T>,
): Promise<{ res: Response; json: unknown }> {
  const url = `${ctx.baseUrl}${options.path}${buildQuery(options.query)}`;
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(ctx.extraHeaders ?? {}),
    ...(ctx.getExtraHeaders?.() ?? {}),
  };

  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  if (options.auth) {
    const token = await ctx.getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const res = await fetchWithTimeout(
    url,
    {
      method: options.method,
      headers,
      body:
        options.body !== undefined ? JSON.stringify(options.body) : undefined,
    },
    ctx.timeoutMs,
  );

  let json: unknown;
  const text = await res.text();
  if (text.length === 0) {
    json = undefined;
  } else {
    try {
      json = JSON.parse(text) as unknown;
    } catch {
      throw new ChotcaApiError(
        "INVALID_JSON",
        "Response body is not valid JSON",
        res.status,
      );
    }
  }

  return { res, json };
}

// Idempotent methods are safe to replay: the request either never reached the
// server, or was processed and only the response was lost — re-sending yields
// the same result. POST is excluded (could create a duplicate).
const RETRYABLE_METHODS: ReadonlySet<HttpMethod> = new Set([
  "GET",
  "PATCH",
  "DELETE",
]);
const MAX_ATTEMPTS = 3;

// Transport-level failure: fetch rejected with no HTTP response (e.g. a stale
// keep-alive socket — RN/Android surfaces this as TypeError "Network request
// failed"). Excludes ChotcaApiError (has a status) and AbortError (our timeout
// — replaying would just wait out the whole window again).
function isRetryableTransportError(err: unknown): boolean {
  if (err instanceof ChotcaApiError) return false;
  if (err instanceof Error && err.name === "AbortError") return false;
  return err instanceof Error;
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

async function performJson<T>(
  ctx: RequestContext,
  options: JsonRequestOptions<T>,
): Promise<{ json: unknown; status: number }> {
  const reqInfo: RequestLogInfo = {
    method: options.method,
    path: options.path,
    query: options.query,
    auth: options.auth,
  };
  safeCall(() => ctx.onRequest?.(reqInfo));

  const maxAttempts = RETRYABLE_METHODS.has(options.method) ? MAX_ATTEMPTS : 1;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const startedAt = Date.now();
    try {
      let { res, json } = await sendOnce(ctx, options);

      // On 401 for an authed request, give the host one chance to refresh the
      // access token; if it returns true we retry once with the new token.
      if (res.status === 401 && options.auth && ctx.onUnauthorized) {
        const refreshed = await ctx.onUnauthorized();
        if (refreshed === true) {
          ({ res, json } = await sendOnce(ctx, options));
        }
      }

      if (res.status === 401 && options.auth) {
        const body = parseApiErrorBody(json);
        const err = body
          ? ChotcaApiError.fromBody(body, res.status)
          : new ChotcaApiError("UNAUTHORIZED", "Unauthorized", res.status);
        safeCall(() =>
          ctx.onError?.({
            ...reqInfo,
            status: res.status,
            durationMs: Date.now() - startedAt,
            error: err,
          }),
        );
        throw err;
      }

      if (!res.ok) {
        const body = parseApiErrorBody(json);
        const err = body
          ? ChotcaApiError.fromBody(body, res.status)
          : new ChotcaApiError("HTTP_ERROR", res.statusText, res.status);
        safeCall(() =>
          ctx.onError?.({
            ...reqInfo,
            status: res.status,
            durationMs: Date.now() - startedAt,
            error: err,
          }),
        );
        throw err;
      }

      safeCall(() =>
        ctx.onResponse?.({
          ...reqInfo,
          status: res.status,
          durationMs: Date.now() - startedAt,
        }),
      );
      return { json, status: res.status };
    } catch (err) {
      // Replay idempotent requests on a transport-level drop; the retry opens a
      // fresh connection, bypassing the dead pooled socket.
      if (isRetryableTransportError(err) && attempt < maxAttempts) {
        await sleep(200 * attempt);
        continue;
      }
      // HTTP error paths above already fired onError before throwing a
      // ChotcaApiError; only emit here for transport-level failures.
      if (!(err instanceof ChotcaApiError)) {
        safeCall(() =>
          ctx.onError?.({
            ...reqInfo,
            durationMs: Date.now() - startedAt,
            error: err,
          }),
        );
      }
      throw err;
    }
  }

  // Unreachable: every iteration returns or throws. Present for the type checker.
  throw new Error("performJson: retry loop exited without a result");
}

export async function requestJson<T>(
  ctx: RequestContext,
  options: JsonRequestOptions<T>,
): Promise<T> {
  const { json, status } = await performJson(ctx, options);

  if (json === undefined && options.allowEmpty) {
    return undefined as T;
  }

  return parseSuccessEnvelope(json, options.dataSchema, status).data;
}

export async function requestJsonWithMeta<T>(
  ctx: RequestContext,
  options: JsonRequestOptions<T>,
): Promise<{ data: T; meta?: PaginationMeta }> {
  const { json, status } = await performJson(ctx, options);

  if (json === undefined && options.allowEmpty) {
    return { data: undefined as T };
  }

  return parseSuccessEnvelope(json, options.dataSchema, status);
}
